// @givmo/compliance — what the Filings page, the Calendar and the filing panel say about each filing
// (CCM-R1-INTAKE (a); D-CCM-21). Pure functions over the decoded records; no React, no reads.
//
// ══ ONE FILING, TWO RECORDS — AND WHICH ONE A SCREEN READS (design go D1, 2026-09-30) ═══════════
//
// A library filing keeps its record in two places that do not follow each other on the backend:
//
//   • the OBLIGATION ROW, which the import, `record_filing` and `record_submission` write, and
//     which the occurrences (the legacy calendar rows) are computed from;
//   • the DURABLE CYCLES, one per year or licence period, which the per-cycle doors write (the
//     closeout, the sent act, the filing as sent, attachments, an earlier year).
//
// A row-level confirmation never reaches a cycle (`_sync_renewal_cycles` copies no receipt), and a
// per-cycle act never moves the row (CCM-BE18 records the reminders half). So every screen here
// reads a year's facts from its CYCLE FIRST, and takes the row's record only for the year no cycle
// carries. CCM-BE19 (minted) makes a row-level confirmation close its cycle; until it lands this
// fallback is what keeps the list, the calendar and the year tiles saying the same thing.
//
// ⚠ NOTHING HERE COMPUTES A DATE. Every date shown is one the backend served (`due_on`, a recorded
// act, a period end). What this file decides is WHICH served fact a screen leads with — the latest
// recorded act, the soonest open year — never what a date should be.

import {
  isReceiptBacked,
  waitsOnTheFirstPeriod,
  type ComplianceObligation,
  type ComplianceOccurrence,
  type ComplianceProfile,
  type FilingFamily,
  type RegistrationImport,
} from './compliance-decode';
import {
  FILING_FAMILIES,
  FILING_FAMILY_NAMES,
  isCycleFiled,
  isOwnFilingEditable,
  isOwnFilingShown,
  ownFilingStatus,
  type FilingGroup,
  type OwnFiling,
  type OwnFilingCycle,
  type RenewalCycle,
} from './compliance-filings-decode';
import type { CaseFollowThrough } from './compliance-submission-decode';

// ── References ─────────────────────────────────────────────────────────────────────────────────

/** Which record a filing on the page is: a library filing (an obligation) or the organization's own. */
export type FilingRef =
  | { kind: 'library'; id: string }
  | { kind: 'organization_recorded'; id: string };

/** A stable key for a filing across both kinds (React keys, group membership). */
export function filingKey(ref: FilingRef): string {
  return `${ref.kind}:${ref.id}`;
}

// ── Recorded acts ──────────────────────────────────────────────────────────────────────────────

/** One act the organization recorded: a filing with its confirmation, or a send (never a filing). */
export interface RecordedAct {
  kind: 'filed' | 'sent';
  /** The date recorded for the act. */
  on: string;
  /**
   * A filing's only: the close of the window it came after, where the record serves one
   * (`filed_after_window_closed_on`, CCM-R1-WINDOW-LABEL-B), and on the row's filing only where the
   * filing has no years (`rowActOf`, `compliance-panel-model.ts`). The panel's year tile states it;
   * the Filings list's Last filing cell does not (cut 1007-2's design go, Q3).
   */
  afterWindowClosedOn?: string;
}

/** The later of two acts; on the same date, a filing outranks a send (it is the further record). */
export function laterAct(
  a: RecordedAct | undefined,
  b: RecordedAct | undefined,
): RecordedAct | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  if (a.on !== b.on) return a.on > b.on ? a : b;
  return a.kind === 'filed' ? a : b;
}

/** What one cycle records: its filing (with the confirmation) or, failing that, its send. */
export function cycleAct(cycle: RenewalCycle): RecordedAct | undefined {
  if (isCycleFiled(cycle) && cycle.recordedFiledOn !== undefined) {
    return { kind: 'filed', on: cycle.recordedFiledOn };
  }
  if (cycle.submissionRecordedOn !== undefined)
    return { kind: 'sent', on: cycle.submissionRecordedOn };
  return undefined;
}

/**
 * What the obligation ROW records today: a receipt-backed filing WITH its document, or a recorded
 * send. A window close, a dismissal or nothing records no act.
 */
export function obligationRowAct(obligation: ComplianceObligation): RecordedAct | undefined {
  if (
    isReceiptBacked(obligation.filingRecordState) &&
    obligation.confirmationArtifactId !== undefined &&
    obligation.recordedFiledOn !== undefined
  ) {
    return { kind: 'filed', on: obligation.recordedFiledOn };
  }
  if (
    obligation.filingRecordState === 'submission_recorded' &&
    obligation.submissionRecordedOn !== undefined
  ) {
    return { kind: 'sent', on: obligation.submissionRecordedOn };
  }
  return undefined;
}

/**
 * The cycles a filing's history may show: the current ones, plus a retired one only where it holds a
 * record (a licence period a new expiry replaced keeps its receipts; an empty retired one is noise).
 */
export function shownCycles(cycles: readonly RenewalCycle[]): RenewalCycle[] {
  return cycles.filter((cycle) => cycle.isCurrent || cycleAct(cycle) !== undefined);
}

/**
 * A library filing's latest recorded act: across its cycles first, and the row's record beside them
 * (the D1 fallback for the year no cycle carries). The later of all of them leads.
 */
export function libraryLastAct(
  obligation: ComplianceObligation,
  cycles: readonly RenewalCycle[],
): RecordedAct | undefined {
  let latest = obligationRowAct(obligation);
  for (const cycle of shownCycles(cycles)) latest = laterAct(latest, cycleAct(cycle));
  return latest;
}

/** What one own period records: its filing (with the confirmation) or, failing that, its send. */
function ownCycleAct(cycle: OwnFilingCycle): RecordedAct | undefined {
  if (cycle.filed && cycle.recordedFiledOn !== undefined) {
    return { kind: 'filed', on: cycle.recordedFiledOn };
  }
  if (cycle.submissionRecordedOn !== undefined)
    return { kind: 'sent', on: cycle.submissionRecordedOn };
  return undefined;
}

/**
 * An own filing's latest recorded act, across every period it has.
 *
 * ⚠ The list read's `latest_cycle` alone cannot answer it: recording the LATEST period sent or filed
 * opens the next, so once anything is recorded the latest period is the open one. The periods come
 * from the filing's detail read; the list's latest period is read beside them.
 */
export function ownLastAct(
  filing: OwnFiling,
  cycles: readonly OwnFilingCycle[] = [],
): RecordedAct | undefined {
  let latest: RecordedAct | undefined;
  for (const cycle of [filing.latestCycle, ...cycles]) {
    if (cycle !== undefined) latest = laterAct(latest, ownCycleAct(cycle));
  }
  return latest;
}

// ── The next date ──────────────────────────────────────────────────────────────────────────────

/** The deadline shapes whose date waits on an anchor only the organization has. */
const ANCHOR_BASES: readonly string[] = [
  'anniversary_of_registration',
  'last_day_of_anniversary_month',
  'license_cycle_expiry',
  'registration_period_end_plus_interval',
];

/** Of those, the ones a licence's expiry answers (the rest take a registration date). */
const LICENCE_BASES: readonly string[] = [
  'license_cycle_expiry',
  'registration_period_end_plus_interval',
];

/** Which date a filing waiting on the organization needs. */
export type AnchorKind = 'registration_date' | 'license_expiry';

export function anchorKindFor(dueDateBasis: string): AnchorKind {
  return LICENCE_BASES.includes(dueDateBasis) ? 'license_expiry' : 'registration_date';
}

/** What a filing's Next date cell, and its place on the Calendar, say. */
export type NextDate =
  /** A served date: a library cycle's or occurrence's `due_on`, or an own period's (`own`). */
  | {
      kind: 'dated';
      dueOn: string;
      own: boolean;
      cycle?: RenewalCycle;
      occurrence?: ComplianceOccurrence;
      /**
       * CCM-R1-INTAKE-MDEXT-OPEN: the date has passed and the year is still open, held so by its
       * automatic extension with nothing recorded for it. The date still leads (Q6), marked N1.
       */
      datePassed?: true;
    }
  /** The date waits on a date only the organization has (a registration date or a licence expiry). */
  | { kind: 'needs-date'; anchor: AnchorKind; dueDateBasis: string }
  /** No fiscal year end is recorded, so nothing year-relative can be worked out. */
  | { kind: 'needs-fiscal-year-end' }
  /** A one-time duty, due before first soliciting, recorded as filed. */
  | { kind: 'filed-once' }
  /** A one-time duty, due before first soliciting, with nothing recorded. */
  | { kind: 'before-first-solicitation' }
  /** Givmo's library has no date for it (no deadline, or a source that states none). */
  | { kind: 'not-supported' }
  /** An own filing the organization stopped tracking (cut 0930-10's `retired`; W8). */
  | { kind: 'no-longer-tracked' }
  /**
   * The first year counts from another filing's sending, which the register doesn't record yet
   * (D-CCM-110 J-F; the served `first_period`): no year is dated until it does.
   */
  | { kind: 'first-period-waits' };

/** Whether a next date waits on something only the organization can give (the Calendar's phrase). */
export function needsInput(next: NextDate): boolean {
  return next.kind === 'needs-date' || next.kind === 'needs-fiscal-year-end';
}

/** Whether the profile is missing the fiscal year end every year-relative date is worked out from. */
export function lacksFiscalYearEnd(profile: ComplianceProfile | undefined): boolean {
  return profile?.missingRequiredParameterKeys.includes('fiscal_year_end') === true;
}

/**
 * The deadline shapes that never carry a date, whatever the organization records: a duty due
 * before it first solicits, and a recurrence its source dates nowhere.
 */
const NEVER_DATED_BASES: readonly string[] = ['before_first_solicitation', 'not_stated_in_source'];

/**
 * What a library filing's date says while no fiscal year end is recorded, from its rule's deadline
 * shapes alone (CCM-R1-INTAKE-NODATE-FYE). The register reads no profile without the year end, so it
 * dates nothing and serves no cycle or occurrence; the shapes come from the filing-options read.
 *   • no deadline → no date ("Givmo doesn't have a date for this yet…"), as with a year end;
 *   • every deadline before first soliciting → "Before you first solicit…": COR-92 needs no year
 *     end to be prepared;
 *   • only shapes that are never dated → no date, as such a filing reads once a year end is recorded;
 *   • anything else → the year-end question: the register dates nothing until it is recorded.
 */
export function withoutFiscalYearEnd(
  shapes: readonly string[],
): 'not-supported' | 'before-first-solicitation' | 'needs-fiscal-year-end' {
  if (shapes.length === 0) return 'not-supported';
  if (shapes.every((basis) => basis === 'before_first_solicitation')) {
    return 'before-first-solicitation';
  }
  if (shapes.every((basis) => NEVER_DATED_BASES.includes(basis))) return 'not-supported';
  return 'needs-fiscal-year-end';
}

/** The key a rule's served deadline shapes are kept under: its jurisdiction and rule. */
export function ruleShapeKey(jurisdictionCode: string, ruleId: string): string {
  return `${jurisdictionCode}|${ruleId}`;
}

/** A library filing's rule's deadline shapes, where the filing-options read served them. */
export function ruleShapesOf(
  shapes: ReadonlyMap<string, readonly string[]> | undefined,
  obligation: Pick<ComplianceObligation, 'jurisdictionCode' | 'ruleId'>,
): readonly string[] | undefined {
  return shapes?.get(ruleShapeKey(obligation.jurisdictionCode, obligation.ruleId));
}

/**
 * A library filing's next date.
 *
 * The soonest CURRENT cycle due today or later that is not closed with its confirmation leads; a
 * dated occurrence with no cycle (none is expected) stands in. Without one, the undated shapes are
 * told apart by their basis, never by their nulls (`describeCycleTiming`'s rule). With no fiscal
 * year end recorded nothing is served to tell them apart by, so `ruleShapes` (the rule's deadline
 * bases, from the filing-options read) decides (`withoutFiscalYearEnd`); not read, it asks for the
 * year end.
 */
/**
 * CCM-R1-INTAKE-MDEXT-OPEN (D-CCM-56): whether a date has passed while its window, counting the
 * automatic extension (the served `windowClosesOn`), is still open on `today` — what N1 marks. The
 * dates are read as served: nothing here works out which date closes the window.
 */
export function datePassedWhileOpen(
  date: { dueOn?: string; windowClosesOn?: string },
  today: string,
): boolean {
  return (
    date.dueOn !== undefined &&
    date.dueOn < today &&
    date.windowClosesOn !== undefined &&
    date.windowClosesOn >= today
  );
}

/**
 * CCM-R1-INTAKE-MDEXT-OPEN: a year whose statutory date has passed while its window, counting its
 * automatic extension, is still open, and nothing is recorded for it. A sent year is not held open: it
 * waits for the agency's confirmation (frame 07), and the next year leads. Per year, so a later row
 * that lets any year be prepared reads the same rule for the year it is given.
 */
export function heldOpenThroughItsExtension(cycle: RenewalCycle, today: string): boolean {
  return (
    datePassedWhileOpen(cycle, today) &&
    cycle.submissionRecordedOn === undefined &&
    !isCycleFiled(cycle)
  );
}

/**
 * CCM-R1-INTAKE-MDEXT-OPEN: the organization's own record that a year's window closed, on that year's
 * date. That year is not held open through its extension: its record says the window closed, and the
 * lapse notice confirms it on that date (the backend's sweep reads the same two fields).
 */
export function windowClosedByItsOwnRecord(
  obligation: ComplianceObligation,
  cycle: RenewalCycle,
): boolean {
  return (
    obligation.filingRecordState === 'window_closed' &&
    cycle.dueOn !== undefined &&
    obligation.refilingWindowClosedOn === cycle.dueOn
  );
}

/**
 * CCM-R1-WINDOW-LABEL-D: whether the obligation ROW's own record closes a year. A late filing recorded
 * on the row names the year it was for (the served `filedAfterWindowClosedCycleId`), whatever year its
 * date alone selected; otherwise the year whose occurrence bears the row's record does (D1's fallback:
 * a confirmation recorded on the row never reaches its cycle, but the occurrence it bears carries it).
 * One rule for the year tiles, "Next due" and the years read overdue, and no date compared here.
 */
export function closedByTheRow(
  obligation: ComplianceObligation,
  occurrences: readonly ComplianceOccurrence[],
): (cycle: RenewalCycle) => boolean {
  const named = obligation.filedAfterWindowClosedCycleId;
  if (named !== undefined) return (cycle) => cycle.cycleId === named;
  const bearing = new Set(
    occurrences
      .filter((occurrence) => isReceiptBacked(occurrence.filingRecordState))
      .map((occurrence) => `${occurrence.cycleKey}|${occurrence.deadlineIndex}`),
  );
  return (cycle) => bearing.has(`${cycle.cycleKey}|${cycle.deadlineIndex}`);
}

/**
 * CCM-BE24-B-COVERED-YEARS (D-CCM-81 (B), D-CCM-85): the year whose filing covers `cycle`, as the
 * backend marks it (`coveredByCycleId`): after "It was wrong", a year moved under a filing recorded on
 * the earlier year end, which covers more than half of it. Found among the filing's own years, or not
 * at all. A covered year shows only by its absence in Next due, the Overdue cards, the Calendar and
 * Filings (Q3), and on its History tile by C1 (`coveredYearLine`).
 */
export function coveringYear(
  cycle: RenewalCycle,
  cycles: readonly RenewalCycle[],
): RenewalCycle | undefined {
  const id = cycle.coveredByCycleId;
  return id === undefined ? undefined : cycles.find((candidate) => candidate.cycleId === id);
}

/**
 * CCM-R1-LATE-FILINGS (D-CCM-62, L1): the years read "Overdue", oldest first. A current year whose
 * window, counting its automatic extension (the served `windowClosesOn`; its own date where the
 * organization recorded the window closed then), closed before `today` with nothing recorded for it:
 * no filing (awaiting its confirmation or not), no send, and no row act that names the year. A dated
 * fact about the register, never a reading of standing: a filing made outside Givmo and never recorded
 * here reads overdue too.
 */
export function overdueCycles(
  obligation: ComplianceObligation,
  cycles: readonly RenewalCycle[],
  occurrences: readonly ComplianceOccurrence[],
  today: string,
): RenewalCycle[] {
  const closed = closedByTheRow(obligation, occurrences);
  return cycles
    .filter((cycle) => {
      if (!cycle.isCurrent || cycle.dueOn === undefined) return false;
      const closesOn = windowClosedByItsOwnRecord(obligation, cycle)
        ? cycle.dueOn
        : (cycle.windowClosesOn ?? cycle.dueOn);
      return (
        closesOn < today &&
        cycle.submissionRecordedOn === undefined &&
        cycle.recordedFiledOn === undefined &&
        !isCycleFiled(cycle) &&
        !closed(cycle) &&
        coveringYear(cycle, cycles) === undefined
      );
    })
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''));
}

/**
 * CCM-R1-LATE-FILINGS (D-CCM-62): the year a form, its signing or its send is opened for (the
 * `?cycle=` an overdue year's links carry, {@link FILING_YEAR_SEARCH_KEY}), where the filing holds it as
 * a current year, else the next date's. Any year is prepared, signed and sent from its own year.
 */
export function withChosenCycle(
  next: NextDate,
  cycles: readonly RenewalCycle[],
  cycleId: string | undefined,
): NextDate {
  if (cycleId === undefined) return next;
  const cycle = cycles.find((candidate) => candidate.isCurrent && candidate.cycleId === cycleId);
  if (cycle?.dueOn === undefined) return next;
  return { kind: 'dated', dueOn: cycle.dueOn, own: false, cycle };
}

/**
 * CCM-BE24-B (W2): the years held open through their automatic extension beside the next date's own,
 * oldest first. Under "It changed", on the day of the change, the earlier year end's last year and
 * the short year can both be open (one to November 15, the other to December 15): "Next due" shows
 * each. Empty everywhere else, so a filing with one open year shows as it always did.
 */
export function alsoOpenCycles(
  obligation: ComplianceObligation,
  cycles: readonly RenewalCycle[],
  occurrences: readonly ComplianceOccurrence[],
  next: NextDate,
  today: string,
): RenewalCycle[] {
  if (next.kind !== 'dated' || next.cycle === undefined || next.datePassed !== true) return [];
  const leading = next.cycle.cycleId;
  const closedOnTheRow = closedByTheRow(obligation, occurrences);
  return cycles
    .filter(
      (cycle) =>
        cycle.cycleId !== leading &&
        cycle.isCurrent &&
        cycle.dueOn !== undefined &&
        heldOpenThroughItsExtension(cycle, today) &&
        !windowClosedByItsOwnRecord(obligation, cycle) &&
        !closedOnTheRow(cycle) &&
        coveringYear(cycle, cycles) === undefined,
    )
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''));
}

export function libraryNextDate(
  obligation: ComplianceObligation,
  cycles: readonly RenewalCycle[],
  occurrences: readonly ComplianceOccurrence[],
  profile: ComplianceProfile | undefined,
  today: string,
  ruleShapes?: readonly string[],
): NextDate {
  // D-CCM-58 (U14): a filing whose tracking stopped shows no new dates: the Filings page says "No
  // longer tracked", the Calendar doesn't list it, and its panel says who stopped it.
  if (obligation.trackingStoppedAt !== undefined) return { kind: 'no-longer-tracked' };
  // D1's fallback, and the year a late filing on the row names (CCM-R1-WINDOW-LABEL-D): a year the
  // row's own record closes is closed too.
  const closedOnTheRow = closedByTheRow(obligation, occurrences);
  const open = cycles
    .filter(
      (cycle) =>
        cycle.isCurrent &&
        cycle.dueOn !== undefined &&
        (cycle.dueOn >= today ||
          (heldOpenThroughItsExtension(cycle, today) &&
            !windowClosedByItsOwnRecord(obligation, cycle))) &&
        !isCycleFiled(cycle) &&
        !closedOnTheRow(cycle) &&
        coveringYear(cycle, cycles) === undefined,
    )
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''));
  const first = open[0];
  if (first?.dueOn !== undefined)
    return {
      kind: 'dated',
      dueOn: first.dueOn,
      own: false,
      cycle: first,
      ...(first.dueOn < today ? { datePassed: true as const } : {}),
    };

  // An occurrence with a date and no cycle of its own (a shape `_NO_CYCLE_DEADLINE_BASES` keeps
  // out of the cycles, should one ever carry a date).
  const cycleKeys = new Set(cycles.map((cycle) => `${cycle.cycleKey}|${cycle.deadlineIndex}`));
  const datedOccurrence = occurrences
    .filter(
      (occurrence) =>
        occurrence.dueOn !== undefined &&
        occurrence.dueOn >= today &&
        !cycleKeys.has(`${occurrence.cycleKey}|${occurrence.deadlineIndex}`) &&
        !isReceiptBacked(occurrence.filingRecordState),
    )
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''))[0];
  if (datedOccurrence?.dueOn !== undefined) {
    return { kind: 'dated', dueOn: datedOccurrence.dueOn, own: false, occurrence: datedOccurrence };
  }

  if (lacksFiscalYearEnd(profile)) {
    const shape = ruleShapes !== undefined ? withoutFiscalYearEnd(ruleShapes) : undefined;
    if (shape === 'not-supported') return { kind: 'not-supported' };
    if (shape === 'before-first-solicitation') {
      return obligationRowAct(obligation)?.kind === 'filed'
        ? { kind: 'filed-once' }
        : { kind: 'before-first-solicitation' };
    }
    return { kind: 'needs-fiscal-year-end' };
  }

  // D-CCM-110 J-F: a first year that counts from a sending the register doesn't record yet. Its one
  // undated year says so (the served `first_period`); no cycle is kept for it.
  if (occurrences.some(waitsOnTheFirstPeriod)) return { kind: 'first-period-waits' };

  const bases = [
    ...cycles
      .filter((cycle) => cycle.isCurrent)
      .map((cycle) => ({
        basis: cycle.dueDateBasis,
        missing: cycle.dueOn === undefined && cycle.uncomputableReason !== undefined,
      })),
    ...occurrences.map((occurrence) => ({
      basis: occurrence.dueDateBasis,
      missing: occurrence.dueOn === undefined && occurrence.uncomputableReason !== undefined,
    })),
  ];
  const waiting = bases.find((entry) => entry.missing && ANCHOR_BASES.includes(entry.basis));
  if (waiting !== undefined) {
    return {
      kind: 'needs-date',
      anchor: anchorKindFor(waiting.basis),
      dueDateBasis: waiting.basis,
    };
  }
  if (bases.length > 0 && bases.every((entry) => entry.basis === 'before_first_solicitation')) {
    return obligationRowAct(obligation)?.kind === 'filed'
      ? { kind: 'filed-once' }
      : { kind: 'before-first-solicitation' };
  }
  return { kind: 'not-supported' };
}

/** An own filing's next date: its latest period's, when nothing is recorded for it yet. */
export function ownNextDate(filing: OwnFiling): NextDate {
  if (ownFilingStatus(filing) === 'retired') return { kind: 'no-longer-tracked' };
  const cycle = filing.latestCycle;
  if (cycle?.dueOn !== undefined && !cycle.filed && cycle.submissionRecordedOn === undefined) {
    return { kind: 'dated', dueOn: cycle.dueOn, own: true };
  }
  if (cycle?.dueOn !== undefined && filing.cadence === 'one_time') {
    return cycle.filed ? { kind: 'filed-once' } : { kind: 'dated', dueOn: cycle.dueOn, own: true };
  }
  // The backend opens the next period when the latest is recorded, so a recorded latest period with
  // no successor is a one-time filing or a read that has not caught up; its own date stands.
  return cycle?.dueOn !== undefined
    ? { kind: 'dated', dueOn: cycle.dueOn, own: true }
    : { kind: 'not-supported' };
}

// ── The Filings page's rows ────────────────────────────────────────────────────────────────────

/** One row of the Filings page (frames 13–17). */
export interface FilingRow {
  ref: FilingRef;
  key: string;
  /** The first line: the jurisdiction, or the agency the organization wrote for its own filing. */
  headline: string;
  /** The second line: the filing's title, where there is a separate one. */
  subtitle?: string;
  /**
   * The family whose group it sits in by default. A family this build cannot read is placed with
   * Other filings — the catch-all, which claims no kind — so a filing is never left off the page.
   */
  family: FilingFamily;
  /** The organization's own number for it, the latest correction where one was recorded. */
  number?: string;
  lastAct?: RecordedAct;
  /** The name a "waiting for … confirmation" line names: the jurisdiction's, for a library filing. */
  confirmationFrom?: string;
  next: NextDate;
  /** Whether the obligation's rule is a report (`annual_report`): its send reads "Last report sent". */
  isReport: boolean;
  /** Nothing on it may be changed: an own filing that is not active (retired, or a state unknown here). */
  readOnly: boolean;
  /** CCM-R1-LATE-FILINGS: a library filing's years read "Overdue", oldest first (`overdueCycles`). */
  overdue?: RenewalCycle[];
  /**
   * CCM-R1-RESULTS-WEB: what is known about a library filing's latest send of any year, from the
   * organization-wide read (the Filings composition sets it). Absent where nothing was sent, or the
   * read is dark: the register's lines then stand.
   */
  latestSend?: CaseFollowThrough;
}

function displayNumber(importRow: RegistrationImport | undefined): string | undefined {
  return importRow?.amendedRegistrationNumber ?? importRow?.recordedRegistrationNumber;
}

/** Whether a library obligation is one the Filings page lists: confirmed, and a filing (it has a family). */
export function isListedLibraryFiling(obligation: ComplianceObligation): boolean {
  return obligation.candidateState === 'confirmed' && obligation.filingFamilyServed;
}

export interface FilingsInput {
  obligations: readonly ComplianceObligation[];
  occurrences: readonly ComplianceOccurrence[];
  cycles: readonly RenewalCycle[];
  imports: readonly RegistrationImport[];
  ownFilings: readonly OwnFiling[];
  /** Each own filing's periods, from its detail read, by filing id. */
  ownFilingCycles?: ReadonlyMap<string, readonly OwnFilingCycle[]>;
  /**
   * Each rule's deadline shapes (`ruleShapeKey` → its deadlines' bases), read from the filing
   * options only while no fiscal year end is recorded (CCM-R1-INTAKE-NODATE-FYE).
   */
  ruleShapes?: ReadonlyMap<string, readonly string[]>;
  profile: ComplianceProfile | undefined;
  /** Today, as an ISO civil date (`YYYY-MM-DD`). */
  today: string;
}

function byObligation<T extends { obligationId: string }>(rows: readonly T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const bucket = map.get(row.obligationId);
    if (bucket === undefined) map.set(row.obligationId, [row]);
    else bucket.push(row);
  }
  return map;
}

/**
 * Every filing the page lists, library and own, in the page's order within a group: jurisdiction
 * (or agency) first, then title (slice 1's order; DESIGN-NOTE-1b §2.1).
 */
export function buildFilingRows(input: FilingsInput): FilingRow[] {
  const cyclesBy = byObligation(input.cycles);
  const occurrencesBy = byObligation(input.occurrences);
  const importBy = new Map(input.imports.map((row) => [row.obligationId, row] as const));
  const rows: FilingRow[] = [];

  for (const obligation of input.obligations) {
    if (!isListedLibraryFiling(obligation)) continue;
    const cycles = cyclesBy.get(obligation.obligationId) ?? [];
    const occurrences = occurrencesBy.get(obligation.obligationId) ?? [];
    const ref: FilingRef = { kind: 'library', id: obligation.obligationId };
    rows.push({
      ref,
      key: filingKey(ref),
      headline: obligation.jurisdictionName || obligation.jurisdictionCode,
      subtitle: obligation.displayTitle,
      family: obligation.filingFamily ?? 'other_filings',
      number: displayNumber(importBy.get(obligation.obligationId)),
      lastAct: libraryLastAct(obligation, cycles),
      confirmationFrom: obligation.jurisdictionName || obligation.jurisdictionCode,
      next: libraryNextDate(
        obligation,
        cycles,
        occurrences,
        input.profile,
        input.today,
        ruleShapesOf(input.ruleShapes, obligation),
      ),
      isReport: obligation.obligationClass === 'annual_report',
      readOnly: false,
      ...(() => {
        const overdue = overdueCycles(obligation, cycles, occurrences, input.today);
        return overdue.length > 0 ? { overdue } : {};
      })(),
    });
  }

  for (const filing of input.ownFilings) {
    // A switched filing gives way to the library filing it became; a retired one stays, read-only.
    if (!isOwnFilingShown(filing)) continue;
    const ref: FilingRef = { kind: 'organization_recorded', id: filing.filingId };
    rows.push({
      ref,
      key: filingKey(ref),
      headline: filing.agency ?? filing.title,
      subtitle: filing.agency !== undefined ? filing.title : undefined,
      family: filing.filingFamily ?? 'other_filings',
      number: filing.number,
      lastAct: ownLastAct(filing, input.ownFilingCycles?.get(filing.filingId)),
      // The agency the organization named answers its confirmation; without one, nobody is named.
      confirmationFrom: filing.agency,
      next: ownNextDate(filing),
      isReport: false,
      readOnly: !isOwnFilingEditable(filing),
    });
  }

  return rows.sort(
    (a, b) =>
      a.headline.localeCompare(b.headline) || (a.subtitle ?? '').localeCompare(b.subtitle ?? ''),
  );
}

// ── The page's groups ──────────────────────────────────────────────────────────────────────────

/** One group as the Filings page shows it, rows already placed and sorted. */
export interface FilingGroupView {
  /** A stable key: the served group's id, or `family:<family>` for a family group not served. */
  key: string;
  /** The served id, which every change to the group names; absent when the groups were not read. */
  groupId?: string;
  /** Set for a family's group; absent for one the organization made. */
  family?: FilingFamily;
  label: string;
  /** The organization's own group: the only kind that can be removed. */
  own: boolean;
  rows: FilingRow[];
}

/**
 * Place every row in its group, in the organization's order (DESIGN-NOTE-1b §2; contract §6.3).
 *
 * A filing the organization moved sits in the group its member row names; every other filing sits in
 * its family's group. The six family groups are served under deterministic ids before any row
 * exists, so a family missing from the read (or a read not made) still gets its group, after the
 * served ones in the families' fixed order, with no id: it can be shown, never changed.
 *
 * Every group comes back, empty ones included; the page shows only those holding a filing (§1.3: no
 * empty family header — an empty section reads as "nothing to file").
 */
export function composeFilingGroups(
  rows: readonly FilingRow[],
  groups: readonly FilingGroup[] | undefined,
): FilingGroupView[] {
  const views: FilingGroupView[] = [];
  const byFamily = new Map<FilingFamily, FilingGroupView>();
  const byId = new Map<string, FilingGroupView>();
  for (const group of groups ?? []) {
    if (group.family !== undefined && byFamily.has(group.family)) continue;
    const view: FilingGroupView = {
      key: group.groupId,
      groupId: group.groupId,
      family: group.family,
      label: group.name ?? (group.family !== undefined ? FILING_FAMILY_NAMES[group.family] : ''),
      own: group.family === undefined,
      rows: [],
    };
    if (view.label.length === 0) continue;
    views.push(view);
    byId.set(group.groupId, view);
    if (group.family !== undefined) byFamily.set(group.family, view);
  }
  for (const family of FILING_FAMILIES) {
    if (byFamily.has(family)) continue;
    const view: FilingGroupView = {
      key: `family:${family}`,
      family,
      label: FILING_FAMILY_NAMES[family],
      own: false,
      rows: [],
    };
    views.push(view);
    byFamily.set(family, view);
  }

  const memberOf = new Map<string, FilingGroupView>();
  for (const group of groups ?? []) {
    const view = byId.get(group.groupId);
    if (view === undefined) continue;
    for (const member of group.members) {
      memberOf.set(filingKey({ kind: member.filingKind, id: member.filingId }), view);
    }
  }
  for (const row of rows) {
    const home = memberOf.get(row.key) ?? byFamily.get(row.family);
    home?.rows.push(row);
  }
  return views;
}

/** What a group's header says beside its name (DESIGN-NOTE-1b §2.5). */
export interface FilingGroupSummary {
  count: number;
  /** The soonest worked-out or organization-set date among the group's filings. */
  nextDueOn?: string;
  /** Filings waiting on a date only the organization can give. */
  needsInput: number;
}

export function summarizeFilingGroup(rows: readonly FilingRow[]): FilingGroupSummary {
  let nextDueOn: string | undefined;
  let waiting = 0;
  for (const row of rows) {
    // MDEXT-OPEN (O3): the group's "Next" keeps to dates still ahead; a year held open by its
    // extension shows its own passed date, marked, on its row.
    if (
      row.next.kind === 'dated' &&
      row.next.datePassed !== true &&
      (nextDueOn === undefined || row.next.dueOn < nextDueOn)
    ) {
      nextDueOn = row.next.dueOn;
    }
    if (needsInput(row.next)) waiting += 1;
  }
  return { count: rows.length, nextDueOn, needsInput: waiting };
}

/** Today as an ISO civil date in the viewer's own time zone — the day their calendar shows. */
export function localIsoDate(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
